import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Card,
  CardBody,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  Alert,
  AlertIcon,
  useColorModeValue,
  Icon,
  Link,
  Button,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import PageHeader from '@/components/common/PageHeader';

const Whitepaper: React.FC = () => {
  const { t, i18n, ready } = useTranslation('whitepaper');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  



  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      

      
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        showBackButton={true}
      />
      
      <Container maxW="4xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* 标题和简介 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h1" size="xl" textAlign="center" color={textColor}>
                  {t('title')}
                </Heading>
                
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      {t('introduction.importantNotice')}
                    </Text>
                    <Text fontSize="sm">
                      {t('introduction.disclaimer')}
                    </Text>
                  </Box>
                </Alert>

                <Box>
                  <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                    {t('introduction.content')}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                    {t('introduction.description')}
                  </Text>
                </Box>

                <Box>
                  <Heading as="h3" size="md" mb={4} color={textColor}>
                    {t('introduction.objectives.title')}
                  </Heading>
                  <List spacing={3}>
                    {ready && Array.isArray(t('introduction.objectives.items', { returnObjects: true })) ? 
                      (t('introduction.objectives.items', { returnObjects: true }) as unknown as any[]).map((item: string, index: number) => (
                        <ListItem key={index} display="flex" alignItems="flex-start">
                          <FiCheckCircle color="var(--chakra-colors-green-500)" style={{ marginTop: "4px" }} />
                          <Text color={textColor}>{item}</Text>
                        </ListItem>
                      ))
                      : (
                        <ListItem display="flex" alignItems="flex-start">
                          <FiCheckCircle color="var(--chakra-colors-green-500)" style={{ marginTop: "4px" }} />
                          <Text color={textColor}>加载中...</Text>
                        </ListItem>
                      )
                    }
                  </List>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* 机制 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h2" size="lg" color={textColor}>
                  {t('mechanism.title')}
                </Heading>
                
                <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                  {t('mechanism.description')}
                </Text>

                <Divider />

                {/* 内盘操作 */}
                <Box>
                  <Heading as="h3" size="md" mb={4} color={textColor}>
                    {t('mechanism.innerDisk.title')}
                  </Heading>
                  
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.innerDisk.recharge.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.innerDisk.recharge.content')}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.innerDisk.referral.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.innerDisk.referral.content')}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.innerDisk.locked.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.innerDisk.locked.content')}
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                <Divider />

                {/* 外盘操作 */}
                <Box>
                  <Heading as="h3" size="md" mb={4} color={textColor}>
                    {t('mechanism.outerDisk.title')}
                  </Heading>
                  
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.outerDisk.price.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.outerDisk.price.content')}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.outerDisk.repurchase.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.outerDisk.repurchase.content')}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        {t('mechanism.outerDisk.buffering.title')}
                      </Text>
                      <Text color={textColor}>
                        {t('mechanism.outerDisk.buffering.content')}
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Text color={textColor}>
                    {t('mechanism.supply')}
                  </Text>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* 代币经济学 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h2" size="lg" color={textColor}>
                  {t('tokenomics.title')}
                </Heading>
                
                <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                  {t('tokenomics.description')}
                </Text>

                <Divider />

                {/* 供应 */}
                <Box>
                  <Heading as="h3" size="md" mb={4} color={textColor}>
                    {t('tokenomics.supply.supplyStructure')}
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <Text color={textColor}>
                      {t('tokenomics.supply.total')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.supply.circulating')}
                    </Text>
                  </VStack>
                </Box>

                <Divider />

                {/* 奖励结构 */}
                <Box>
                  <Heading as="h3" size="md" mb={4} color={textColor}>
                    {t('tokenomics.rewards.title')}
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <Text color={textColor}>
                      {t('tokenomics.rewards.double')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.rewards.referral')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.rewards.fees')}
                    </Text>
                  </VStack>
                </Box>

                <Divider />

                {/* 其他机制 */}
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={2} color={textColor}>
                      {t('tokenomics.reserve.title')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.reserve.content')}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2} color={textColor}>
                      {t('tokenomics.destruction.title')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.destruction.content')}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={2} color={textColor}>
                      {t('tokenomics.governance.title')}
                    </Text>
                    <Text color={textColor}>
                      {t('tokenomics.governance.content')}
                    </Text>
                  </Box>
                </VStack>

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text color={textColor}>
                    {t('tokenomics.loop')}
                  </Text>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* 风险 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h2" size="lg" color={textColor}>
                  {t('risks.title')}
                </Heading>
                
                <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                  {t('risks.description')}
                </Text>

                <Divider />

                <VStack spacing={4} align="stretch">
                  {ready && Array.isArray(t('risks.items', { returnObjects: true })) ? 
                    (t('risks.items', { returnObjects: true }) as unknown as any[]).map((risk: any, index: number) => (
                      <Box key={index} p={4} border="1px" borderColor={borderColor} borderRadius="md">
                        <HStack spacing={3} mb={2}>
                          <FiAlertTriangle color="orange.500" />
                          <Text fontWeight="bold" color={textColor}>
                            {risk.title}
                          </Text>
                        </HStack>
                        <Text color={textColor} pl={8}>
                          {risk.content}
                        </Text>
                      </Box>
                    ))
                    : (
                      <Box p={4} border="1px" borderColor={borderColor} borderRadius="md">
                        <Text color={textColor}>加载中...</Text>
                      </Box>
                    )
                  }
                </VStack>

                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <Text color={textColor}>
                    {t('risks.mitigations')}
                  </Text>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* 混合方面 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h2" size="lg" color={textColor}>
                  {t('hybrid.title')}
                </Heading>
                
                <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                  {t('hybrid.description')}
                </Text>

                <Divider />

                <List spacing={3}>
                  {ready && Array.isArray(t('hybrid.aspects', { returnObjects: true })) ? 
                    (t('hybrid.aspects', { returnObjects: true }) as unknown as any[]).map((aspect: string, index: number) => (
                      <ListItem key={index} display="flex" alignItems="flex-start">
                        <FiCheckCircle color="var(--chakra-colors-blue-500)" style={{ marginTop: "4px" }} />
                        <Text color={textColor}>{aspect}</Text>
                      </ListItem>
                    ))
                    : (
                      <ListItem display="flex" alignItems="flex-start">
                        <FiCheckCircle color="var(--chakra-colors-blue-500)" style={{ marginTop: "4px" }} />
                        <Text color={textColor}>加载中...</Text>
                      </ListItem>
                    )
                  }
                </List>

                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Text color={textColor}>
                    {t('hybrid.conclusion')}
                  </Text>
                </Alert>
              </VStack>
            </CardBody>
          </Card>

          {/* 结论 */}
          <Card bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Heading as="h2" size="lg" color={textColor}>
                  {t('conclusion.title')}
                </Heading>
                
                <Text fontSize="lg" lineHeight="1.8" color={textColor}>
                  {t('conclusion.content')}
                </Text>

                <Divider />

                <Box>
                  <Text fontSize="md" color={textColor}>
                    {t('conclusion.technical')}
                  </Text>
                </Box>

                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color={textColor}>
                      {t('conclusion.team')}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      {t('conclusion.date')}
                    </Text>
                  </VStack>
                  
                  <Link href="https://github.com/dor-stable" isExternal>
                    <Badge colorScheme="green" p={2} borderRadius="md">
                      <HStack spacing={2}>
                        <FiExternalLink />
                        <Text>{t('conclusion.github')}</Text>
                      </HStack>
                    </Badge>
                  </Link>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default Whitepaper; 